import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { Form, Input, Select, message } from "antd"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { useRecoilValue } from "recoil"
import { isPayingUserSelector, userAtom } from "@/atoms/userAtom"
import { PriceConfiguration, PriceConfigurationForm } from "@/types/drivesAppSettings"
import { currencyAtom } from "@/atoms/currencyAtom"
import DisplayPrice from "@/UI/DataDisplay/DisplayPrice"
import { useDriverSettings } from "@/hooks/useDriverSettings"
import { DistanceUnit } from "@/types/localization.types"
import { useTeams } from "@/hooks/useTeams"
import { capitalize, isEmpty, isEqual } from "lodash"
import { LabelWithTooltip } from "@/UI/Labels/LabelWithTooltip"
import { useClients } from "@/hooks/useClients"
import { useFlags } from "flagsmith/react"

interface DriverPayFormProps {
  config?: PriceConfiguration
  type?: "clients" | "teams"
  setIsModalVisible?: (isModalVisible: boolean) => void
}

const DriverPayForm: React.FC<DriverPayFormProps> = ({
  config,
  setIsModalVisible,
  type = "teams",
}) => {
  const { t } = useTranslation()

  const flags = useFlags(["nash_edg_demo"])

  const [form] = Form.useForm()
  const isPayingUser = useRecoilValue(isPayingUserSelector)
  const { preference } = useRecoilValue(userAtom)
  const { isLoading, updateDriverPaySettings, driverSettings } = useDriverSettings()
  const { teams } = useTeams()
  // teams that are not already in the driver pay settings
  const teamsOptions = teams
    ?.filter(
      (team) =>
        !driverSettings.driverPay
          ?.filter((c) => c.id !== config?.id)
          .some((c) => c.teams?.some((t) => t.id === team.id))
    )
    .map((team) => ({
      value: team.id,
      key: team.id,
      label: capitalize(team?.name),
    }))
  const { clients, updateClientPay, clientPay } = useClients()
  const clientOptions = clients
    ?.filter(
      (client) =>
        !clientPay
          ?.filter((c) => c.id !== config?.id)
          .some((c) => c.clients?.some((cc) => cc.id === client.id))
    )
    .map((client) => ({
      value: client.id,
      key: client.id,
      label: capitalize(client?.name),
    }))
  const currency = useRecoilValue(currencyAtom)

  const emptyPriceConfig: PriceConfiguration = {
    id: "",
    basePrice: 0,
    perDelivery: 0,
    perKm: 0,
    minKm: 0,
    perMi: 0,
    minMi: 0,
  }

  const basePrice = Form.useWatch("basePrice", form)
  const perDelivery = Form.useWatch("perDelivery", form)
  const perKm = Form.useWatch("perKm", form)
  const minKm = Form.useWatch("minKm", form)
  const perMi = Form.useWatch("perMi", form)
  const minMi = Form.useWatch("minMi", form)
  const teamIDs = Form.useWatch("teamIDs", form)
  const clientIDs = Form.useWatch("clientIDs", form)
  const isModifying =
    basePrice !== config?.basePrice ||
    perDelivery !== config?.perDelivery ||
    perKm !== config?.perKm ||
    minKm !== config?.minKm ||
    perMi !== config?.perMi ||
    minMi !== config?.minMi ||
    !isEqual(
      teamIDs,
      config?.teams?.map((team) => team.id)
    ) ||
    !isEqual(
      clientIDs,
      config?.clients?.map((client) => client.id)
    )

  useEffect(() => {
    const formPayload = config || emptyPriceConfig
    form.setFieldsValue({
      ...formPayload,
      teamIDs: formPayload.teams?.map((team) => team.id),
      clientIDs: formPayload.clients?.map((client) => client.id),
    })
  }, [config])

  const onFinish = async (formValues: PriceConfigurationForm) => {
    const updateFn = type === "teams" ? updateDriverPaySettings : updateClientPay
    try {
      await updateFn({
        id: config?.id || "",
        basePrice: Number(formValues.basePrice),
        perDelivery: Number(formValues.perDelivery),
        perKm: Number(formValues.perKm),
        minKm: Number(formValues.minKm),
        perMi: Number(formValues.perMi),
        minMi: Number(formValues.minMi),
        teams: teamIDs?.map((teamID: number) => ({ id: teamID })),
        clients: clientIDs?.map((clientID: number) => ({ id: clientID })),
      })
      message.success(t("common.form.updatedPreferencesSuccess"))
      setIsModalVisible && setIsModalVisible(false)
    } catch (err) {
      message.error(t("common.form.updatedPreferencesFailure"))
    }
  }

  // component to show example price based on different order types
  const PriceExamples: React.FC = () => {
    const examples = [
      {
        label: preference.distanceUnit === DistanceUnit.MI ? "0.5 mi = " : "0.5 km = ",
        deliveries: 1,
        km: 0.5,
        mi: 0.5,
      },
      {
        label: preference.distanceUnit === DistanceUnit.MI ? "3 mi = " : "3 km = ",
        deliveries: 1,
        km: 3,
        mi: 3,
      },
      {
        label: preference.distanceUnit === DistanceUnit.MI ? "2x 10 mi = " : "2x 10 km = ",
        deliveries: 2,
        km: 10,
        mi: 10,
      },
    ]

    return (
      <div className="flex flex-col gap-y-2">
        <p className="font-semibold">{t("profile.DriverPaySettings.priceExamples")}</p>
        {examples.map((example, i) => (
          <div key={i} className="flex gap-x-2 items-center">
            <p>{example.label}</p>
            <DisplayPrice
              currencyCode={currency.code}
              amount={
                (basePrice || 0) * 1 +
                perDelivery * example.deliveries +
                (preference.distanceUnit === DistanceUnit.MI
                  ? perMi * Math.max(Math.ceil(example.mi) - minMi, 0)
                  : perKm * Math.max(Math.ceil(example.km) - minKm, 0))
              }
              withDecimals={true}
            />
          </div>
        ))}
      </div>
    )
  }

  return (
    <Form
      id="driver-app-settings-form"
      className="flex flex-col w-full gap-y-1"
      form={form}
      onFinish={onFinish}
      disabled={!isPayingUser}
    >
      {teamsOptions.length > 0 && type === "teams" && (
        <div className="flex flex-col gap-y-1">
          <Form.Item name="teamIDs" required>
            <Select
              id="select-teams"
              className="!cursor-pointer max-w-[300px]"
              options={teamsOptions}
              showSearch={false}
              mode="multiple"
              placeholder={`${t("common.selectTeam")}`}
              disabled={isEmpty(teams)}
              dropdownStyle={{ zIndex: 3001 }}
            />
          </Form.Item>
        </div>
      )}
      {clientOptions.length > 0 && type === "clients" && (
        <div className="flex flex-col gap-y-1">
          <Form.Item name="clientIDs" required>
            <Select
              id="select-clients"
              className="!cursor-pointer max-w-[300px]"
              options={clientOptions}
              showSearch={false}
              mode="multiple"
              placeholder={`${t("common.selectClients")}`}
              disabled={isEmpty(clients)}
              dropdownStyle={{ zIndex: 3001 }}
            />
          </Form.Item>
        </div>
      )}
      {type === "teams" && (
        <div className="flex flex-col gap-y-1 !max-w-[140px]">
          <LabelWithTooltip
            label={t("profile.DriverPaySettings.basePrice")}
            tooltipContent={t("profile.DriverPaySettings.basePriceTooltip")}
          />
          <Form.Item name="basePrice">
            <Input
              name="basePrice"
              className="rounded-md border-[#d9d9d9]"
              placeholder={t("profile.DriverPaySettings.basePrice")}
              min={0}
              step={0.01}
              type="number"
              suffix={currency?.symbol}
            />
          </Form.Item>
        </div>
      )}
      <div className="flex flex-col gap-y-1 !max-w-[140px]">
        <LabelWithTooltip
          label={t("profile.DriverPaySettings.perDelivery")}
          tooltipContent={t("profile.DriverPaySettings.perDeliveryTooltip")}
        />
        <Form.Item name="perDelivery">
          <Input
            name="perDelivery"
            className="rounded-md border-[#d9d9d9]"
            placeholder={t("profile.DriverPaySettings.perDelivery")}
            min={0}
            step={0.01}
            type="number"
            suffix={currency?.symbol}
          />
        </Form.Item>
      </div>
      <div
        className={`${
          preference.distanceUnit === DistanceUnit.KM ? "flex" : "hidden"
        } flex-row gap-x-2`}
      >
        <div className="flex flex-col gap-y-1 !max-w-[140px]">
          <LabelWithTooltip
            label={t("profile.DriverPaySettings.perKm")}
            tooltipContent={t("profile.DriverPaySettings.perKmTooltip")}
          />
          <Form.Item name="perKm">
            <Input
              name="perKm"
              className="rounded-md border-[#d9d9d9]"
              placeholder={t("profile.DriverPaySettings.perKm")}
              min={0}
              step={0.01}
              type="number"
              suffix={currency?.symbol}
            />
          </Form.Item>
        </div>
        <div className="flex flex-col gap-y-1 !max-w-[140px]">
          <LabelWithTooltip
            label={t("profile.DriverPaySettings.startWithKm")}
            tooltipContent={t("profile.DriverPaySettings.startWithKmTooltip")}
          />
          <Form.Item name="minKm">
            <Input
              name="minKm"
              className="rounded-md border-[#d9d9d9]"
              placeholder={t("profile.DriverPaySettings.startWithKm")}
              min={0}
              step={1}
              type="number"
              suffix={DistanceUnit.KM}
            />
          </Form.Item>
        </div>
      </div>
      <div
        className={`${
          preference.distanceUnit === DistanceUnit.MI ? "flex" : "hidden"
        } flex-row gap-x-2`}
      >
        <div className="flex flex-col gap-y-1 !max-w-[140px]">
          <LabelWithTooltip
            label={t("profile.DriverPaySettings.perMi")}
            tooltipContent={t("profile.DriverPaySettings.perMiTooltip")}
          />
          <Form.Item name="perMi">
            <Input
              name="perMi"
              className="rounded-md border-[#d9d9d9]"
              placeholder={t("profile.DriverPaySettings.perMi")}
              min={0}
              step={0.01}
              type="number"
              suffix={currency?.symbol}
            />
          </Form.Item>
        </div>
        <div className="flex flex-col gap-y-1 !max-w-[140px]">
          <LabelWithTooltip
            label={t("profile.DriverPaySettings.startWithMi")}
            tooltipContent={t("profile.DriverPaySettings.startWithMiTooltip")}
          />
          <Form.Item name="minMi">
            <Input
              name="minMi"
              className="rounded-md border-[#d9d9d9]"
              placeholder={t("profile.DriverPaySettings.startWithMi")}
              min={0}
              step={1}
              type="number"
              suffix={DistanceUnit.MI}
            />
          </Form.Item>
        </div>
      </div>
      {flags.nash_edg_demo.enabled && (
        <>
          <div className="flex flex-col gap-y-1 !max-w-[140px]">
            <LabelWithTooltip
              label={t("profile.DriverPaySettings.commissionFee")}
              tooltipContent={t("profile.DriverPaySettings.commissionFeeTooltip")}
            />
            <Form.Item name="commissionFee" initialValue={0}>
              <Input
                name="commissionFee"
                className="rounded-md border-[#d9d9d9]"
                placeholder={"5"}
                min={0}
                step={1}
                type="number"
                suffix={"%"}
              />
            </Form.Item>
          </div>
          <div className="flex flex-col gap-y-1 !max-w-[140px]">
            <LabelWithTooltip
              label={t("profile.DriverPaySettings.returnFee")}
              tooltipContent={t("profile.DriverPaySettings.returnFeeTooltip")}
            />
            <Form.Item name="returnFee" initialValue={0}>
              <Input
                name="returnFee"
                className="rounded-md border-[#d9d9d9]"
                placeholder={"5"}
                min={0}
                step={0.01}
                type="number"
                suffix={currency?.symbol}
              />
            </Form.Item>
          </div>
          <div className="flex flex-col gap-y-4">
            <div>
              <LabelWithTooltip
                label={t("profile.DriverPaySettings.waitingFee")}
                tooltipContent={t("profile.DriverPaySettings.waitingFeeTooltip")}
              />
              <div className="flex flex-row gap-x-2 gap-y-1 flex-wrap items-center">
                <p>When more than</p>
                <Form.Item name="waitTime" initialValue={0} className="!mb-0">
                  <Input
                    name="waitTime"
                    className="rounded-none border-[#d9d9d9] max-w-[100px]"
                    style={{ borderLeft: 0, borderRight: 0, borderTop: 0 }}
                    placeholder={"0"}
                    min={0}
                    step={0.01}
                    type="number"
                    suffix={<p className="text-[12px]">{t("min")}</p>}
                  />
                </Form.Item>
                <p>of waiting time then pay</p>
                <Form.Item name="waitingFee" initialValue={0} className="!mb-0">
                  <Input
                    name="waitingFee"
                    className="rounded-none border-[#d9d9d9] max-w-[100px]"
                    style={{ borderLeft: 0, borderRight: 0, borderTop: 0 }}
                    placeholder={"0"}
                    min={0}
                    step={0.01}
                    type="number"
                    suffix={<p className="text-[12px]">{currency?.symbol}</p>}
                  />
                </Form.Item>
              </div>
            </div>
            <div>
              <LabelWithTooltip
                label={t("profile.DriverPaySettings.bulkyBonus")}
                tooltipContent={t("profile.DriverPaySettings.bulkyBonusTooltip")}
              />
              <div className="flex flex-row gap-x-2 flex-wrap items-center">
                <p>When more than</p>
                <Form.Item name="bulkyBonus" initialValue={0} className="!mb-0">
                  <Input
                    name="weightLimit"
                    className="rounded-none border-[#d9d9d9] max-w-[100px]"
                    style={{ borderLeft: 0, borderRight: 0, borderTop: 0 }}
                    placeholder={"0"}
                    min={0}
                    step={0.01}
                    type="number"
                    suffix={<p className="text-[12px]">{t("Kg")}</p>}
                  />
                </Form.Item>
                <p>then pay</p>
                <Form.Item name="bulkyFee" initialValue={0} className="!mb-0">
                  <Input
                    name="bulkyFee"
                    className="rounded-none border-[#d9d9d9] max-w-[100px]"
                    style={{ borderLeft: 0, borderRight: 0, borderTop: 0 }}
                    placeholder={"0"}
                    min={0}
                    step={0.01}
                    type="number"
                    suffix={<p className="text-[12px]">{currency?.symbol}</p>}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </>
      )}
      <div className="mt-4">
        <PriceExamples />
      </div>
      <div className="flex gap-x-4 gap-y-6 mt-12">
        <KosmoButton
          onClick={() => {
            form.setFieldsValue(config || emptyPriceConfig)
            setIsModalVisible && setIsModalVisible(false)
          }}
          type="default"
          classname="font-semibold"
          isDisabled={isLoading || !isPayingUser}
        >
          {t("common.cancel")}
        </KosmoButton>
        <KosmoButton
          type="primary"
          classname="font-semibold"
          htmlType="submit"
          isLoading={isLoading && isModifying}
          isDisabled={isLoading || !isModifying || !isPayingUser}
        >
          {t("common.save")}
        </KosmoButton>
      </div>
    </Form>
  )
}

export default DriverPayForm
